import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './form-view-modal.html';
import type {
  FormDefinition,
  FormFieldDefinition,
  FormViewDefinition,
} from '../services/api.types';
import { createFormView, updateFormView } from '../services/api.service';
import { ConfirmDialog } from './confirm-dialog';

type TimelineMode = 'point' | 'interval';
type ViewBucket = 'record' | 'day' | 'week' | 'month';
type ChartType = 'line' | 'bar';
type PointMarkers = 'all' | 'formDates' | 'none';

type EditableMetric = {
  id: string;
  label: string;
  formula: string;
  unit: string;
};

type CurrentViewDefinition = {
  version?: number;
  formTitle?: string;
  timeline?: {
    mode?: TimelineMode;
    valueSource?: string;
    allocation?: string;
    incomplete?: string;
  };
  bucket?: ViewBucket;
  groupByField?: string | null;
  metrics?: Array<Partial<EditableMetric>>;
  chart?: {
    type?: ChartType;
    yAxisLabel?: string;
    pointMarkers?: PointMarkers;
  };
};

@customElement({ name: 'form-view-modal', template })
export class FormViewModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public forms: FormDefinition[] = [];
  @bindable public saved!: (view: FormViewDefinition) => void;
  @bindable public closed!: () => void;
  @bindable public viewToEdit: FormViewDefinition | null = null;

  public title = '';
  public formTitle = '';
  public timelineMode: TimelineMode = 'point';
  public bucket: ViewBucket = 'record';
  public groupByField = '';
  public chartType: ChartType = 'line';
  public yAxisLabel = '';
  public pointMarkers: PointMarkers = 'all';
  public metrics: EditableMetric[] = [];
  public availableFields: FormFieldDefinition[] = [];
  public numericFields: FormFieldDefinition[] = [];
  public saving = false;
  public error = '';

  private _keyHandler: ((event: KeyboardEvent) => void) | null = null;
  private _isBound = false;
  private _nextMetricId = 1;
  private _initialState = '';
  private _closeConfirmationOpen = false;

  public get isEditMode(): boolean {
    return this.viewToEdit !== null;
  }

  public get hasChanges(): boolean {
    return this._initialState !== '' && this.stateSnapshot() !== this._initialState;
  }

  public get selectedForm(): FormDefinition | null {
    return this.forms.find(form => form.title === this.formTitle) ?? null;
  }

  public binding(): void {
    this._isBound = true;
    this.resetFromBinding();
  }

  public bound(): void {
    this.captureInitialState();
  }

  public attached(): void {
    this._keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') void this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    if (this._keyHandler)
      document.removeEventListener('keydown', this._keyHandler);
  }

  public viewToEditChanged(): void {
    if (this._isBound) {
      this.resetFromBinding();
      this.captureInitialState();
    }
  }

  public formsChanged(): void {
    if (!this._isBound) return;

    if (!this.viewToEdit && !this.formTitle && this.forms.length > 0)
      this.formTitle = this.forms[0].title;
    else if (this.viewToEdit &&
             this.forms.length > 0 &&
             this.formTitle &&
             !this.selectedForm)
      this.formTitle = '';

    this.refreshAvailableFields();
    if (this.metrics.length === 0 && this.numericFields.length > 0)
      this.addMetric();
  }

  public onFormChanged(): void {
    this.groupByField = '';
    this.metrics = [];
    this.refreshAvailableFields();
    if (this.numericFields.length > 0) this.addMetric();
  }

  public onTimelineChanged(): void {
    if (this.timelineMode === 'interval' && this.bucket === 'record') {
      this.bucket = 'week';
      if (this.pointMarkers === 'all') this.pointMarkers = 'formDates';
    } else if (this.timelineMode === 'point' && !this.bucket) {
      this.bucket = 'record';
    }
  }

  public addMetric(): void {
    const field = this.numericFields[0];
    const formula = field
      ? `${this.timelineMode === 'interval' ? 'sum' : 'latest'}(${this.fieldReference(field.name)})`
      : '';
    this.metrics = [
      ...this.metrics,
      {
        id: `metric-${this._nextMetricId++}`,
        label: field?.name ?? '',
        formula,
        unit: '',
      },
    ];
  }

  public removeMetric(index: number): void {
    this.metrics = this.metrics.filter((_metric, metricIndex) => metricIndex !== index);
  }

  public appendFieldReference(metric: EditableMetric, field: FormFieldDefinition): void {
    const reference = this.fieldReference(field.name);
    const aggregate = metric.formula.match(
      /^(sum|average|minimum|maximum|latest)\((.*)\)$/s,
    );
    if (aggregate) {
      const inner = aggregate[2].trim();
      metric.formula = `${aggregate[1]}(${inner ? `${inner} + ` : ''}${reference})`;
      return;
    }
    metric.formula = metric.formula.trim()
      ? `${metric.formula} + ${reference}`
      : reference;
  }

  public setMetricTemplate(metric: EditableMetric, functionName: string): void {
    const field = this.numericFields[0];
    metric.formula = field
      ? `${functionName}(${this.fieldReference(field.name)})`
      : `${functionName}()`;
  }

  public async close(): Promise<void> {
    if (this.saving || this._closeConfirmationOpen) return;
    if (this.hasChanges) {
      this._closeConfirmationOpen = true;
      try {
        const discard = await ConfirmDialog.confirm(
          this.i18n.tr('documents.form.unsavedChanges'),
          this.i18n.tr('documents.form.unsavedChangesTitle'),
        );
        if (!discard) return;
      } finally {
        this._closeConfirmationOpen = false;
      }
    }
    this.closed();
  }

  public async save(): Promise<void> {
    this.error = '';
    const normalizedTitle = this.title.trim();
    if (!normalizedTitle) {
      this.error = this.i18n.tr('forms.views.form.titleRequired');
      return;
    }
    if (!this.formTitle) {
      this.error = this.i18n.tr('forms.views.form.formRequired');
      return;
    }
    if (this.metrics.length === 0) {
      this.error = this.i18n.tr('forms.views.form.metricRequired');
      return;
    }

    for (const [index, metric] of this.metrics.entries()) {
      metric.label = metric.label.trim();
      metric.formula = metric.formula.trim();
      metric.unit = metric.unit.trim();
      if (!metric.label) {
        this.error = this.i18n.tr('forms.views.form.metricLabelRequired', {
          index: index + 1,
        });
        return;
      }
      if (!metric.formula) {
        this.error = this.i18n.tr('forms.views.form.metricFormulaRequired', {
          label: metric.label,
        });
        return;
      }
    }

    this.saving = true;
    try {
      const definition: CurrentViewDefinition = {
        version: 2,
        formTitle: this.formTitle,
        timeline: {
          mode: this.timelineMode,
          valueSource: 'previous',
          allocation: 'proportional',
          incomplete: 'exclude',
        },
        bucket: this.bucket,
        groupByField: this.groupByField || null,
        metrics: this.metrics.map(metric => ({
          id: metric.id,
          label: metric.label,
          formula: metric.formula,
          unit: metric.unit,
        })),
        chart: {
          type: this.chartType,
          yAxisLabel: this.yAxisLabel.trim(),
          pointMarkers: this.pointMarkers,
        },
      };
      const dto = {
        title: normalizedTitle,
        viewType: 'line',
        definitionJson: JSON.stringify(definition),
      };
      const view = this.isEditMode && this.viewToEdit
        ? await updateFormView(this.viewToEdit.id, dto)
        : await createFormView(dto);
      this.saved(view);
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      this.saving = false;
    }
  }

  private resetFromBinding(): void {
    this.title = this.viewToEdit?.title ?? '';
    this.formTitle = '';
    this.timelineMode = 'point';
    this.bucket = 'record';
    this.groupByField = '';
    this.chartType = 'line';
    this.yAxisLabel = '';
    this.pointMarkers = 'all';
    this.metrics = [];
    this.error = '';
    this._nextMetricId = 1;

    if (this.viewToEdit) {
      try {
        const definition = JSON.parse(this.viewToEdit.definitionJson) as
          CurrentViewDefinition & {
            seriesField?: string;
            valueField?: string;
          };
        this.formTitle = definition.formTitle ?? '';
        if (definition.metrics) {
          this.timelineMode = definition.timeline?.mode ?? 'point';
          this.bucket = definition.bucket
            ?? (this.timelineMode === 'interval' ? 'week' : 'record');
          this.groupByField = definition.groupByField ?? '';
          this.chartType = definition.chart?.type ?? 'line';
          this.yAxisLabel = definition.chart?.yAxisLabel ?? '';
          this.pointMarkers = definition.chart?.pointMarkers
            ?? (this.timelineMode === 'interval' ? 'formDates' : 'all');
          this.metrics = definition.metrics.map(metric => ({
            id: metric.id || `metric-${this._nextMetricId++}`,
            label: metric.label ?? '',
            formula: metric.formula ?? '',
            unit: metric.unit ?? '',
          }));
        } else if (definition.valueField) {
          this.groupByField = definition.seriesField ?? '';
          this.metrics = [{
            id: 'value',
            label: definition.valueField,
            formula: `latest(${this.fieldReference(definition.valueField)})`,
            unit: '',
          }];
        }
      } catch {
        this.error = this.i18n.tr('forms.views.form.invalidDefinition');
      }
    }

    if (this.forms.length > 0 && this.formTitle && !this.selectedForm) {
      this.formTitle = '';
      this.groupByField = '';
      this.metrics = [];
    }
    if (!this.formTitle && !this.viewToEdit && this.forms.length > 0)
      this.formTitle = this.forms[0].title;

    this.refreshAvailableFields();
    if (this.metrics.length === 0 && this.numericFields.length > 0)
      this.addMetric();
    this.onTimelineChanged();
  }

  private captureInitialState(): void {
    this._initialState = this.stateSnapshot();
  }

  private stateSnapshot(): string {
    return JSON.stringify({
      title: this.title,
      formTitle: this.formTitle,
      timelineMode: this.timelineMode,
      bucket: this.bucket,
      groupByField: this.groupByField,
      chartType: this.chartType,
      yAxisLabel: this.yAxisLabel,
      pointMarkers: this.pointMarkers,
      metrics: this.metrics.map(metric => ({
        id: metric.id,
        label: metric.label,
        formula: metric.formula,
        unit: metric.unit,
      })),
    });
  }

  private refreshAvailableFields(): void {
    const fields = this.selectedForm?.fields
      .slice()
      .sort((a, b) => a.position - b.position) ?? [];
    this.availableFields = fields;
    this.numericFields = fields.filter(field => field.dataType === 'number');
  }

  private fieldReference(name: string): string {
    return `[${name.replaceAll(']', ']]')}]`;
  }
}
